/**
 * Alpine.js Block & Delete Modal Component
 *
 * Two-step flow:
 *   1. Confirm — block + delete the thread immediately on click.
 *   2. Reason  — after the block succeeds, optionally pick a category
 *                / leave a note that gets filed as a moderator report
 *                via /v1/report.
 *
 * Triggered by window.openBlockDeleteModal({ threadId, contactUid, contactUsername }).
 */

import { lockBodyScroll, unlockBodyScroll } from './scroll-lock.js';
import { BLOCK_FLOW_ORIGIN } from './report-origin.js';
import { CHILD_SAFETY_REASON } from './report-reasons.js';

/**
 * Report::BLOCK_REPORT_PLACEHOLDER, the free-text marker this flow prepends to
 * the reason. It carries the same fact as the `origin` field, in
 * reporter-controlled text instead of a column, and it stays because it is the
 * only provenance a report filed before that column existed carries. Its
 * readers span several statuses and time windows, so none of them can drop it
 * while such rows are still in their range.
 *
 * Retiring it takes a backfill, not patience, and the runbook for that lives
 * with the column rather than here -- see the docblock above the ORIGIN_*
 * constants in app/Model/Moderation/Report.php, which a backend reader will
 * actually find.
 *
 * tests/unit/Model/Moderation/ReportOriginJsMirrorTest.php pins the literal
 * against its PHP source.
 */
const BLOCK_REPORT_PLACEHOLDER = '[reported while blocking]';

export function blockDeleteModal() {
    return {
        isOpen: false,
        step: 'confirm',
        threadId: '',
        contactUid: '',
        contactUsername: '',
        reasonSel: '',
        reason: '',
        isSubmitting: false,
        reasonOptions: [],
        titles: { confirm: '', reason: '', done: '' },

        init() {
            const optsEl = this.$el.querySelector('script[data-block-reason-options]');
            try {
                this.reasonOptions = optsEl ? JSON.parse(optsEl.textContent) : [];
            } catch (e) {
                this.reasonOptions = [];
            }
            const titlesEl = this.$el.querySelector('script[data-block-titles]');
            try {
                this.titles = titlesEl ? JSON.parse(titlesEl.textContent) : this.titles;
            } catch (e) { /* keep defaults */ }
            window.openBlockDeleteModal = (opts) => this.open(opts || {});
        },

        open({ threadId, contactUid, contactUsername }) {
            this.threadId = String(threadId || '');
            this.contactUid = String(contactUid || '');
            this.contactUsername = String(contactUsername || '');
            this.reasonSel = '';
            this.reason = '';
            this.step = 'confirm';
            this.isSubmitting = false;
            this.isOpen = true;
            lockBodyScroll();
            this.$nextTick(() => {
                if (window.LucideIcons) window.LucideIcons.initialize(this.$el);
            });
        },

        close() {
            if (this.isSubmitting) return;
            this.isOpen = false;
            unlockBodyScroll();
        },

        toggleReason(value) {
            if (value === '__no_problem') {
                this.close();
                return;
            }
            this.reasonSel = this.reasonSel === value ? '' : value;
        },

        async confirmBlock() {
            if (this.isSubmitting || !this.threadId) return;
            this.isSubmitting = true;

            const errMsg = window.tOr('common.pm.block_error', "Couldn't block. Please try again.");

            try {
                const data = await new Promise((resolve, reject) => {
                    if (typeof window.jQuery === 'undefined') {
                        reject(new Error('jQuery unavailable'));
                        return;
                    }
                    window.jQuery.ajax({
                        type: 'POST',
                        url: '/app/messages/delete-thread',
                        data: {
                            thread: this.threadId,
                            block_user: 1,
                        },
                        dataType: 'json',
                    }).done((data) => {
                        // Auth/CSRF/onboarding gate returns {redirect: url} for a
                        // lapsed session. The global ajaxComplete handler
                        // navigates, so do not resolve into the block-failed path.
                        if (data && typeof data.redirect === 'string' && data.redirect) {
                            return;
                        }
                        resolve(data);
                    }).fail(() => reject(new Error(errMsg)));
                });

                if (!data || !data.success) {
                    throw new Error((data && data.error) || errMsg);
                }

                if (typeof window.removeThreadRow === 'function') {
                    // Routes through the shared removal so the in-flight-poll guard
                    // is set, the pane closes only when this thread is open, and the
                    // Message Requests UI (#3039) is notified to drop its row.
                    window.removeThreadRow(this.threadId);
                } else if (window.jQuery) {
                    window.jQuery('#thread_' + this.threadId).remove();
                    if (window.pm_view_mode === 'chat') {
                        window.jQuery('#threads_main').removeClass('threads--open');
                        window.jQuery('#threads_right').empty();
                    }
                }

                this.step = 'reason';
                this.$nextTick(() => {
                    if (window.LucideIcons) window.LucideIcons.initialize(this.$el);
                });
            } catch (error) {
                if (window.showError) {
                    window.showError(error.message || errMsg);
                } else {
                    alert(error.message || errMsg);
                }
            } finally {
                this.isSubmitting = false;
            }
        },

        /** The child-safety confirmation carries emergency guidance and a Take It Down link, so it stays open. */
        showChildSafetyDone() {
            this.step = 'child_safety_done';
            this.$nextTick(() => this.$refs.childSafetyDone?.focus());
        },

        async sendReport() {
            if (this.isSubmitting || !this.reasonSel || !this.contactUid) return;
            this.isSubmitting = true;
            // The reason can change while the request is in flight. The confirmation follows what was sent.
            const submittedReason = this.reasonSel;

            try {
                const token = window.getApiToken
                    ? await window.getApiToken()
                    : (window.getAuthToken ? window.getAuthToken() : null);
                if (!token) throw new Error(window.tOr('common.auth_required', 'Authentication required'));

                const body = new URLSearchParams({
                    entity_type: 'user',
                    target_id: this.contactUid,
                    target_uid: this.contactUid,
                    reason_sel: submittedReason,
                    origin: BLOCK_FLOW_ORIGIN,
                });
                const note = this.reason && this.reason.trim();
                if (note) body.set('reason', BLOCK_REPORT_PLACEHOLDER + ' ' + note);
                else body.set('reason', BLOCK_REPORT_PLACEHOLDER);

                const response = await fetch('/v1/report', {
                    method: 'POST',
                    headers: {
                        'X-AUTH-TOKEN': token,
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    credentials: 'include',
                    body,
                });

                if (!response.ok) {
                    const data = await response.json().catch(() => ({}));

                    // Already reported inside the re-report cooldown. The block
                    // itself already went through, so close out normally rather
                    // than reporting a failure the user cannot act on.
                    if (data && data.error && data.error.code === 'report_duplicate') {
                        // A child-safety reporter still gets the emergency guidance and the Take It Down link.
                        if (submittedReason === CHILD_SAFETY_REASON) {
                            this.showChildSafetyDone();
                            return;
                        }
                        if (window.showSuccess) {
                            window.showSuccess(data.error.message || window.tOr('legacy.report.recent_duplicate', "You already reported this. We're looking into it."));
                        }
                        this.isOpen = false;
                        unlockBodyScroll();
                        return;
                    }

                    throw new Error((data && data.error && data.error.message) || window.tOr('legacy.report.submit_failed', 'Failed to submit report'));
                }

                if (submittedReason === CHILD_SAFETY_REASON) {
                    this.showChildSafetyDone();
                    return;
                }
                if (window.showSuccess) {
                    window.showSuccess(window.tOr('legacy.report.submitted', 'Report submitted. Thank you for helping keep InterPals safe.'));
                }
                this.isOpen = false;
                unlockBodyScroll();
            } catch (error) {
                if (window.showError) {
                    window.showError(error.message || window.tOr('legacy.report.submit_failed', 'Failed to submit report'));
                } else {
                    alert(error.message || window.tOr('legacy.report.submit_failed', 'Failed to submit report'));
                }
            } finally {
                this.isSubmitting = false;
            }
        },
    };
}

if (typeof window !== 'undefined') {
    window.blockDeleteModal = blockDeleteModal;
}

export default blockDeleteModal;
